/**
 * wizardData.js
 *
 * Canonical wizard data module for the Tool Finder wizard. Consumed by the
 * six wizard step components (D3b - D3f and D5) under
 * `src/components/wizard/`.
 *
 * Source hierarchy (per port-first rule, 17 May 2026):
 *   - APPLICATIONS, EQUIPMENT_CLASSES_TH, ROCK_CONDITIONS: ported from the
 *     canonical Top Hammer ToolFinder v4 constants module
 *     (`tophammer-toolfinder-v4/src/constants/index.js` and
 *     `tophammer-toolfinder-v4/src/config/constants.js`). Ids, labels, and
 *     downstream-filter keys match TH TF v4 exactly.
 *   - DRILLING_METHODS: extended from TH TF v4's DRILLING_TYPES to include
 *     the full mining-method enum (TH / DTH available; ITH / Rotary /
 *     Raiseboring disabled with `comingSoon` reason). Uppercase ids ('TH',
 *     'DTH', ...) match Sandvik item-class codes used in business docs.
 *   - EQUIPMENT_CLASSES_DTH: NEW. Derived from the DTH domain knowledge
 *     base (`tophammer-dth-domain.md`, "DOWN THE HOLE (DTH) DRILLING
 *     SYSTEM" and "DTH Hammer Compatibility" sections). The same
 *     conditional sub-step renders `shankChoices` for DTH and
 *     `threadChoices` for TH.
 *   - THREAD_DETAILS: ported verbatim from TH TF v4 StepThreadChoice.jsx
 *     lines 11-79 (D3d-update, 17 May 2026). Plus ST58 / ST68 entries
 *     derived from TF v4 THREAD_SYSTEMS.underground (constants/index.js
 *     line 528) because EQUIPMENT_CLASSES_TH.tube references them.
 *
 * Helpers:
 *   - getEquipmentClassById(id, productLine): productLine-aware lookup.
 *   - getChoicesForClass(eqClass, productLine): returns the thread choices
 *     array for TH or the shank choices array for DTH.
 *
 * @module data/wizardData
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D-prep)
 */

// ============================================================================
// APPLICATIONS — Step 1 (Path A: Explore)
// Ported verbatim from TH TF v4 src/constants/index.js lines 164-171.
// Used by the wizard matcher's `application_c` filter; do NOT diverge.
// ============================================================================

export const APPLICATIONS = [
  { id: 'construction',  label: 'Construction',         icon: 'construction' },
  { id: 'tunneling',     label: 'Tunneling',            icon: 'subway' },
  { id: 'well_drilling', label: 'Well drilling',        icon: 'water_drop' },
  { id: 'underground',   label: 'Underground drilling', icon: 'hardware' },
  { id: 'surface',       label: 'Surface drilling',     icon: 'landscape' },
  { id: 'quarrying',     label: 'Quarrying',            icon: 'terrain' },
];

// ============================================================================
// DRILLING_METHODS — Step 2 (Path A: Explore)
// Extends TH TF v4 DRILLING_TYPES (src/constants/index.js lines 177-180,
// which only carried `top_hammer` + `dth`) with the full mining-method enum
// per Vandana's domain correction (17 May 2026). Drilling method is the
// *process* taxonomy; productLine is the *catalog* taxonomy. Today they map
// 1:1 for TH and DTH; ITH / Rotary / Raiseboring will get their own
// productLine values when catalog data lands.
//
// Uppercase ids match Sandvik item-class codes (TH, DTH) used in
// business / KB docs.
// ============================================================================

export const DRILLING_METHODS = [
  { id: 'TH',          label: 'Top Hammer',     available: true,  productLine: 'TH'  },
  { id: 'DTH',         label: 'Down the Hole',  available: true,  productLine: 'DTH' },
  { id: 'ITH',         label: 'In the Hole',    available: false, disabledReason: 'Coming soon — catalog in development' },
  { id: 'Rotary',      label: 'Rotary',         available: false, disabledReason: 'Coming soon — rotary catalog in development' },
  { id: 'Raiseboring', label: 'Raiseboring',    available: false, disabledReason: 'Coming soon — raiseboring catalog in development' },
];

// ============================================================================
// EQUIPMENT_CLASSES_TH — Step 3 (Path A: Explore, productLine === 'TH')
// Ported verbatim from TH TF v4 src/constants/index.js
// EQUIPMENT_CLASSES_V2 (lines 203-285). 7 cards covering all 10 thread
// systems. Cards with a `thread` string use the thread directly; cards
// with non-null `threadChoices` open sub-step EQUIPMENT_THREAD_CHOICE.
//
// References (per TH TF v4 source):
//   F006_PathA_Restructure_Mockup_v3.md (approved Feb 25, 2026)
//   Reyes Roodzant, L. (2025) Selection guide for shank adapters V12.0
// ============================================================================

export const EQUIPMENT_CLASSES_TH = [
  // --- Row 1: Standard threaded (single thread each) ---
  {
    id: 'light',
    label: 'Light Drifting',
    thread: 'R32',
    threadChoices: null,
    power: '8–14 kW',
    drills: 'H200, RD300, RD314, RD414',
    productCount: '~257',
    row: 1,
  },
  {
    id: 'medium',
    label: 'Medium Drifting',
    thread: 'T38',
    threadChoices: null,
    power: '10–20 kW',
    drills: 'RD520, RD525, HL1560ST, RD927L',
    productCount: '~370',
    row: 1,
  },
  {
    id: 'heavy',
    label: 'Heavy Drifting',
    thread: 'T45',
    threadChoices: null,
    power: '14–28 kW',
    drills: 'HF1560ST, HL650, HL710',
    productCount: '~204',
    row: 1,
  },
  {
    id: 'extra_heavy',
    label: 'Extra Heavy / Production',
    thread: 'T51',
    threadChoices: null,
    power: '18–28 kW',
    drills: 'HL820T, HF820T, HL650T',
    productCount: '~145',
    row: 1,
  },
  {
    id: 'bolting',
    label: 'Bolting',
    thread: 'R38',
    threadChoices: null,
    power: '8–12 kW',
    drills: 'HL510',
    productCount: '~74',
    row: 1,
  },

  // --- Row 2: Specialty (multiple threadChoices each) ---
  {
    id: 'tube',
    label: 'Tube Drilling',
    thread: null,
    threadChoices: [
      { thread: 'ST58', label: 'ST58 — Medium tubes', desc: '89–152mm hole range', productCount: '~48' },
      { thread: 'ST68', label: 'ST68 — Large tubes',  desc: '102–178mm hole range', productCount: '~40' },
    ],
    power: '18–35 kW',
    drills: 'HL1060T, HL1000',
    productCount: '~88',
    row: 2,
  },
  {
    id: 'premium',
    label: 'Premium / Large Hole',
    thread: null,
    threadChoices: [
      { thread: 'CT55', label: 'CT55 — Curved 55mm',   desc: '89–102mm hole range', productCount: '~6' },
      { thread: 'CT67', label: 'CT67 — Large curved',   desc: '96–165mm hole range', productCount: '~11' },
      { thread: 'GT60', label: 'GT60 — Standard 60mm',  desc: '89–152mm hole range', productCount: '~22' },
      { thread: 'LT90', label: 'LT90 — Extra large',    desc: '102–178mm hole range', productCount: '~1' },
    ],
    power: '20–50 kW',
    drills: 'RD921S, RD1725, RD1840C',
    productCount: 'many',
    row: 2,
  },
];

// ============================================================================
// EQUIPMENT_CLASSES_DTH — Step 3 (Path A: Explore, productLine === 'DTH')
// NEW. Derived from the DTH domain KB at
// Claude-Enterprise-Migration-Package/03-sandvik-agents/.../kb/tophammer-dth-domain.md
// (sections "DOWN THE HOLE (DTH) DRILLING SYSTEM" lines 218-317 +
// "DTH Hammer Compatibility" lines 357-372).
//
// Each class has `shankChoices` (DTH analogue of TH's threadChoices) so the
// same conditional EQUIPMENT_THREAD_CHOICE sub-step can render shanks for
// DTH and threads for TH. Hole-size ranges from KB lines 222-307; shank ↔
// hammer matrix from KB lines 363-372; pin/connection options from KB
// lines 232-294.
// ============================================================================

export const EQUIPMENT_CLASSES_DTH = [
  {
    id: 'rh460',
    label: 'RH460 Series',
    sublabel: 'Compact — 3.5" to 4.5" holes',
    holeSizeRangeMm: { min: 89, max: 127 },
    shankChoices: ['DHD3.5HD', 'DHD340', 'DHD350', 'QLX40'],
    pinOptions: ['API 2 3/8"', 'BECO', 'CUBEX'],
    applications: ['Water well', 'Small quarry', 'Geotech'],
    row: 1,
  },
  {
    id: 'rh510',
    label: 'RH510 Series',
    sublabel: 'Medium — 4" to 5" holes',
    holeSizeRangeMm: { min: 90, max: 165 },
    shankChoices: ['M3', 'M30', 'M4'],
    pinOptions: ['API 2 3/8"', 'API 2 7/8"', 'BECO', 'CUBEX'],
    applications: ['Mining development', 'Mineral exploration', 'Geotech'],
    row: 1,
  },
  {
    id: 'rh560',
    label: 'RH560 Series',
    sublabel: 'Production — 3.5" to 6.5" holes (up to 9" large format)',
    holeSizeRangeMm: { min: 95, max: 241 },
    shankChoices: ['M40', 'M50', 'M60'],
    pinOptions: ['API 3 1/2"', 'API 2 3/8"', 'API 2 7/8"', 'BECO 5 1/4"', 'BECO 6"', 'CUBEX'],
    applications: ['Production blasthole', 'Large quarry', 'Deep well'],
    row: 1,
  },
  {
    id: 'rh570',
    label: 'RH570 Series',
    sublabel: 'Compact-to-heavy — 3.5" to 9.5" holes',
    // Audited from public/data/products-dth.json (18 May 2026):
    //   109 items — 66 spares + 29 bits + 14 hammers.
    //   shankType values: 570-3.5, 570-4, 570-5, 570-6, 570-9.5.
    //   bit diameters: 95-254 mm.
    holeSizeRangeMm: { min: 95, max: 254 },
    shankChoices: ['570-3.5', '570-4', '570-5', '570-6', '570-9.5'],
    pinOptions: ['API 2 3/8"', 'API 3 1/2"'],
    applications: ['Production blasthole', 'Quarry', 'Mining development'],
    row: 2,
  },
];

// ============================================================================
// ROCK_CONDITIONS — Step 5 (optional, all paths)
// Ported from TH TF v4 src/config/constants.js lines 33-38. Four hardness
// levels with MPa ranges, brand colour codes, and bitClassifications
// arrays used by the bit-selection callout (D9).
// ============================================================================

export const ROCK_CONDITIONS = [
  { id: 'soft',      label: 'Soft',      description: '< 100 MPa',     color: '#7FCC5B', bitClassifications: [1, 2, 3] },
  { id: 'medium',    label: 'Medium',    description: '100-180 MPa',   color: '#FFD74B', bitClassifications: [3, 4, 5] },
  { id: 'hard',      label: 'Hard',      description: '180-250 MPa',   color: '#F54B00', bitClassifications: [5, 6, 7] },
  { id: 'very_hard', label: 'Very Hard', description: '> 250 MPa',     color: '#EB0D0D', bitClassifications: [7, 8, 9] },
];

// ============================================================================
// THREAD_DETAILS — visual + descriptive metadata for thread badges.
// Ported verbatim from TH TF v4 StepThreadChoice.jsx lines 11-79 (9 entries).
// Plus MRT-side additions for ST58 / ST68 derived from TH TF v4 constants/
// index.js THREAD_SYSTEMS.underground (line 528) — needed because
// EQUIPMENT_CLASSES_TH.tube.threadChoices references them, but the original
// TF v4 StepThreadChoice never showed them (it ran for rig-derived threads,
// not Path A equipment-class threads).
//
// The `color` field is a raw hex string because it feeds the inline
// backgroundColor style on the thread badge — Tailwind class names cannot
// drive inline styles, and tokenising would require a runtime CSS-var
// lookup. Premium-thread `#A08C64` matches the sandvik-gold token value
// (tailwind.config.js); standard `#1441F5` matches electric-blue. Keep in
// sync if those brand tokens ever shift.
//
// `isPremium: true` flags CT55 / CT67 / LT90. The picker renders a gold
// "Premium" pill badge next to the name when this is set (see TF v4
// StepThreadChoice.jsx lines 135-139).
// ============================================================================

export const THREAD_DETAILS = {
  T38: {
    name: 'T38',
    size: '38mm threads',
    description: 'Light to medium duty drilling',
    holeRange: '38-64 mm',
    color: '#1441F5',
  },
  T45: {
    name: 'T45',
    size: '45mm threads',
    description: 'Medium duty drilling',
    holeRange: '45-89 mm',
    color: '#1441F5',
  },
  T51: {
    name: 'T51',
    size: '51mm threads',
    description: 'Heavy duty drilling',
    holeRange: '51-115 mm',
    color: '#1441F5',
  },
  GT60: {
    name: 'GT60',
    size: '60mm threads',
    description: 'Extra heavy duty drilling',
    holeRange: '60-127 mm',
    color: '#1441F5',
  },
  CT55: {
    name: 'CT55',
    size: 'Curved Thread 55mm',
    description: 'Premium thread for optimized energy transfer',
    holeRange: '64-127 mm',
    color: '#A08C64',
    isPremium: true,
  },
  CT67: {
    name: 'CT67',
    size: 'Curved Thread 67mm',
    description: 'Premium thread for large hole drilling',
    holeRange: '89-152 mm',
    color: '#A08C64',
    isPremium: true,
  },
  LT90: {
    name: 'LT90',
    size: 'Large Thread 90mm',
    description: 'For extra large hole drilling',
    holeRange: '102-178 mm',
    color: '#A08C64',
    isPremium: true,
  },
  R32: {
    name: 'R32',
    size: '32mm round threads',
    description: 'For underground bolting and small holes',
    holeRange: '32-51 mm',
    color: '#1441F5',
  },
  R38: {
    name: 'R38',
    size: '38mm round threads',
    description: 'Underground drilling',
    holeRange: '38-64 mm',
    color: '#1441F5',
  },
  // MRT additions — ST-series underground tube threads.
  ST58: {
    name: 'ST58',
    size: 'Tube Thread 58mm',
    description: 'Underground tube drilling — medium tubes',
    holeRange: '89-152 mm',
    color: '#1441F5',
  },
  ST68: {
    name: 'ST68',
    size: 'Tube Thread 68mm',
    description: 'Underground tube drilling — large tubes',
    holeRange: '102-178 mm',
    color: '#1441F5',
  },
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Look up an equipment class by id, scoped to the active productLine.
 *
 * @param {string} id              Equipment class id (e.g. 'rh460', 'extra_heavy').
 * @param {'TH'|'DTH'} productLine Active product line (uppercase, matches
 *                                 DRILLING_METHODS.id).
 * @returns {Object|null} The equipment class object or null when not found.
 *                       Caller can branch on
 *                       (threadChoices?.length || shankChoices?.length) > 0
 *                       to decide whether the conditional sub-step is
 *                       needed.
 */
export function getEquipmentClassById(id, productLine) {
  if (!id) return null;
  const source = productLine === 'DTH' ? EQUIPMENT_CLASSES_DTH : EQUIPMENT_CLASSES_TH;
  return source.find((cls) => cls.id === id) || null;
}

/**
 * Return the thread / shank choices array for a given equipment class,
 * depending on the active productLine. Used by StepEquipmentThreadChoice
 * (D3d) to render the correct picker.
 *
 * @param {Object|null} eqClass         Equipment class object (or null).
 * @param {'TH'|'DTH'}   productLine    Active product line.
 * @returns {Array} Empty array when the class has no multi-choice sub-step,
 *                  otherwise the threadChoices (TH) or shankChoices (DTH).
 */
export function getChoicesForClass(eqClass, productLine) {
  if (!eqClass) return [];
  if (productLine === 'DTH') return eqClass.shankChoices || [];
  return eqClass.threadChoices || [];
}

// ============================================================================
// MASTER_TAB_REGISTRY (TH) — Results-page tab definitions for Top Hammer.
//
// Ported verbatim from TH TF v4 src/constants/index.js MASTER_TAB_REGISTRY
// (lines 335-401, 9 tabs). Each entry maps a results-page tab to one or more
// product `subcategory` slugs and names the field that carries the thread
// system for that product type. TF v4 keyed off `subcategory`; MRT
// products-tophammer.json carries the same slugs (audit 17 May 2026:
// shank-adapters, extension-rods, mf-guide-rods, drifter-rods, drill-tubes,
// drill-bits, reamer-bits, coupling-sleeves, guide-tubes, pilot-bit-adapters).
// ============================================================================

export const MASTER_TAB_REGISTRY = [
  {
    key: 'shank-adapters',
    label: 'Shank Adapters',
    categories: ['shank-adapters'],
    threadField: 'thread',
    sortOrder: 1,
  },
  {
    key: 'extension-rods',
    label: 'Extension Rods',
    categories: ['mf-guide-rods', 'extension-rods'],
    threadField: 'thread1',
    sortOrder: 2,
  },
  {
    key: 'drifter-rods',
    label: 'Drifter Rods',
    categories: ['drifter-rods'],
    threadField: 'thread1',
    sortOrder: 3,
  },
  {
    key: 'drill-tubes',
    label: 'Drill Tubes',
    categories: ['drill-tubes'],
    threadField: 'thread1',
    sortOrder: 4,
  },
  {
    key: 'drill-bits',
    label: 'Drill Bits',
    categories: ['drill-bits'],
    threadField: 'thread',
    sortOrder: 5,
  },
  {
    key: 'reamer-bits',
    label: 'Reamer Bits',
    categories: ['reamer-bits'],
    threadField: 'thread',
    sortOrder: 6,
  },
  {
    key: 'coupling-sleeves',
    label: 'Coupling Sleeves',
    categories: ['coupling-sleeves'],
    threadField: 'threadF1',
    sortOrder: 7,
  },
  {
    key: 'guide-tubes',
    label: 'Guide Tubes',
    categories: ['guide-tubes'],
    threadField: 'thread1',
    sortOrder: 8,
  },
  {
    key: 'pilot-adapters',
    label: 'Pilot Bit Adapters',
    categories: ['pilot-bit-adapters'],
    threadField: 'threadFemale',
    sortOrder: 9,
  },
];

// ============================================================================
// MASTER_TAB_REGISTRY_DTH — MRT addition for the DTH results-page tabs.
//
// DTH products carry `category` (NOT `subcategory` like TH). Confirmed by
// audit of public/data/products-dth.json (17 May 2026):
//   category values → { dth-hammers: 623, dth-bits: 618, dth-drill-pipes: 179,
//                       other: 7 }
//
// Initial 3 tabs; extend when DTH spare-parts subgrouping is finalised
// (RH460/RH510/RH560 spares might warrant per-series tabs per the DTH KB).
// `threadField: 'shankType'` because the DTH KB defines hammer/bit
// compatibility via shank type (DHD340, M30, M50, ...).
// ============================================================================

export const MASTER_TAB_REGISTRY_DTH = [
  { key: 'dth-bits',        label: 'DTH Bits',     categories: ['dth-bits'],        threadField: 'shankType', sortOrder: 1 },
  { key: 'dth-hammers',     label: 'DTH Hammers',  categories: ['dth-hammers'],     threadField: 'shankType', sortOrder: 2 },
  { key: 'dth-drill-pipes', label: 'Drill Pipes',  categories: ['dth-drill-pipes'], threadField: 'shankType', sortOrder: 3 },
];

// ============================================================================
// PLP_ATTRIBUTES — Fields surfaced on product cards per category.
// Ported verbatim from TH TF v4 src/constants/index.js PLP_ATTRIBUTES
// (lines 408-467).
// ============================================================================

export const PLP_ATTRIBUTES = {
  'shank-adapters': [
    { label: 'Manufacturer', field: 'rockDrillManufacturer' },
    { label: 'Rock drill', field: 'compatibleRockDrill' },
    { label: 'Length', field: 'lengthMm', suffix: ' mm' },
    { label: 'Thread', field: 'thread' },
  ],
  'extension-rods': [
    { label: 'Rod type', field: 'rodType' },
    { label: 'Shank end', field: 'thread1' },
    { label: 'Bit end', field: 'thread2' },
    { label: 'Length', field: 'lengthMm', suffix: ' mm' },
  ],
  'drill-bits': [
    { label: 'Thread', field: 'thread' },
    { label: 'Diameter', field: 'bitDiameterMm', suffix: ' mm' },
    { label: 'Skirt', field: 'skirtDesign' },
    { label: 'Button', field: 'buttonShape' },
  ],
  'coupling-sleeves': [
    { label: 'Thread F1', field: 'threadF1' },
    { label: 'Thread F2', field: 'threadF2' },
    { label: 'Type', field: 'couplingType' },
    { label: 'Diameter', field: 'diameterMm', suffix: ' mm' },
  ],
  'drifter-rods': [
    { label: 'Shank end', field: 'thread1' },
    { label: 'Bit end', field: 'thread2' },
    { label: 'Dimension', field: 'rodDimension' },
    { label: 'Length', field: 'lengthMm', suffix: ' mm' },
  ],
  'reamer-bits': [
    { label: 'Thread', field: 'thread' },
    { label: 'Diameter', field: 'bitDiameterMm', suffix: ' mm' },
    { label: 'Skirt', field: 'skirtDesign' },
    { label: 'Button', field: 'buttonShape' },
  ],
  'guide-tubes': [
    { label: 'Thread 1', field: 'thread1' },
    { label: 'Thread 2', field: 'thread2' },
    { label: 'Diameter', field: 'diameterMm', suffix: ' mm' },
    { label: 'Length', field: 'lengthMm', suffix: ' mm' },
  ],
  'drill-tubes': [
    { label: 'Thread 1', field: 'thread1' },
    { label: 'Thread 2', field: 'thread2' },
    { label: 'Length', field: 'lengthMm', suffix: ' mm' },
    { label: 'Rod type', field: 'rodType' },
  ],
  'pilot-adapters': [
    { label: 'Female thread', field: 'threadFemale' },
    { label: 'Male thread', field: 'threadMale' },
    { label: 'Diameter', field: 'diameterMm', suffix: ' mm' },
    { label: 'Type', field: 'rodType' },
  ],
};

// ============================================================================
// FACET_CONFIG — Filter facets per category for the PLP sidebar.
// Ported verbatim from TH TF v4 src/constants/index.js FACET_CONFIG
// (lines 469-523).
// ============================================================================

export const FACET_CONFIG = {
  'shank-adapters': [
    { id: 'thread', label: 'Thread', field: 'thread' },
    { id: 'rockDrillManufacturer', label: 'Manufacturer', field: 'rockDrillManufacturer' },
    { id: 'compatibleRockDrill', label: 'Rock drill model', field: 'compatibleRockDrill' },
  ],
  'extension-rods': [
    { id: 'rodType', label: 'Rod type', field: 'rodType' },
    { id: 'thread1', label: 'Shank end thread', field: 'thread1' },
    { id: 'thread2', label: 'Bit end thread', field: 'thread2' },
    { id: 'lengthMm', label: 'Length (mm)', field: 'lengthMm' },
  ],
  'drill-bits': [
    { id: 'thread', label: 'Thread', field: 'thread' },
    { id: 'bitDiameterMm', label: 'Diameter (mm)', field: 'bitDiameterMm' },
    { id: 'skirtDesign', label: 'Skirt design', field: 'skirtDesign' },
    { id: 'buttonShape', label: 'Button shape', field: 'buttonShape' },
  ],
  'coupling-sleeves': [
    { id: 'threadF1', label: 'Thread F1', field: 'threadF1' },
    { id: 'threadF2', label: 'Thread F2', field: 'threadF2' },
    { id: 'couplingType', label: 'Coupling type', field: 'couplingType' },
  ],
  'drifter-rods': [
    { id: 'thread1', label: 'Shank end thread', field: 'thread1' },
    { id: 'thread2', label: 'Bit end thread', field: 'thread2' },
    { id: 'rodDimension', label: 'Rod dimension', field: 'rodDimension' },
    { id: 'lengthMm', label: 'Length (mm)', field: 'lengthMm' },
  ],
  'reamer-bits': [
    { id: 'thread', label: 'Thread', field: 'thread' },
    { id: 'bitDiameterMm', label: 'Diameter (mm)', field: 'bitDiameterMm' },
    { id: 'skirtDesign', label: 'Skirt design', field: 'skirtDesign' },
    { id: 'buttonShape', label: 'Button shape', field: 'buttonShape' },
  ],
  'guide-tubes': [
    { id: 'thread1', label: 'Thread', field: 'thread1' },
    { id: 'diameterMm', label: 'Diameter (mm)', field: 'diameterMm' },
    { id: 'lengthMm', label: 'Length (mm)', field: 'lengthMm' },
  ],
  'drill-tubes': [
    { id: 'thread1', label: 'Thread', field: 'thread1' },
    { id: 'lengthMm', label: 'Length (mm)', field: 'lengthMm' },
  ],
  'pilot-adapters': [
    { id: 'threadFemale', label: 'Female thread', field: 'threadFemale' },
    { id: 'threadMale', label: 'Male thread', field: 'threadMale' },
    { id: 'rodType', label: 'Type', field: 'rodType' },
  ],
};

// ============================================================================
// THREAD_SYSTEMS + isPremiumThread — thread classification.
// Ported verbatim from TH TF v4 src/constants/index.js THREAD_SYSTEMS
// (lines 525-538). Premium threads CT55 / CT67 / LT90 drive the gold
// "Premium" badge on the thread picker and the results-page chip.
// ============================================================================

export const THREAD_SYSTEMS = {
  standard: ['R32', 'T38', 'T45', 'T51', 'GT60', 'R38'],
  premium: ['CT55', 'CT67', 'LT90'],
  underground: ['ST58', 'ST68'],
};

export function isPremiumThread(thread) {
  return THREAD_SYSTEMS.premium.includes(thread);
}

// ============================================================================
// getTabRegistryFor — return the correct results-tab registry per product line.
// MRT helper; TF v4 hardcoded MASTER_TAB_REGISTRY everywhere because TF v4
// is TH-only.
// ============================================================================

/**
 * Return the results-page tab registry for the given product line.
 * @param {'TH'|'DTH'|string|null} productLine
 * @returns {Array} MASTER_TAB_REGISTRY_DTH when productLine === 'DTH',
 *                  otherwise MASTER_TAB_REGISTRY (TH path).
 */
export function getTabRegistryFor(productLine) {
  if (productLine === 'DTH') return MASTER_TAB_REGISTRY_DTH;
  return MASTER_TAB_REGISTRY;
}

